import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '@/shared/contexts/AuthContext'
import { supabase } from '@/shared/lib/supabase'
import { Header } from '@/shared/components/layout/Header'
import { StatusBar } from '@/shared/components/layout/StatusBar'

export function ResetPasswordPage() {
  const { updatePassword } = useAuth()
  const navigate = useNavigate()

  const [recoverySession, setRecoverySession] = useState<'pending' | 'ready' | 'invalid'>('pending')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (!supabase) {
      setRecoverySession('invalid')
      return
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setRecoverySession('ready')
    })
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setRecoverySession('ready')
      else setTimeout(() => setRecoverySession(s => s === 'pending' ? 'invalid' : s), 2000)
    })
    return () => subscription.unsubscribe()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.')
      return
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.')
      return
    }
    setIsLoading(true)
    const { error } = await updatePassword(password)
    setIsLoading(false)
    if (error) {
      setError(error.message)
      return
    }
    setSuccess(true)
    setTimeout(() => navigate('/firmar-electronica', { replace: true }), 1500)
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-legal-50 to-legal-100 dark:from-legal-900 dark:to-legal-800 pb-7">
      <Header hideAuthControls />
      <div className="flex-1 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-legal-900 dark:text-white mb-1">Restablecer contraseña</h2>
        </div>

        <div className="bg-white dark:bg-legal-800 rounded-2xl shadow-xl p-8">
          {recoverySession === 'pending' && (
            <div className="text-center text-legal-600 dark:text-legal-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-legal-600 mx-auto mb-3" />
              Validando enlace...
            </div>
          )}

          {recoverySession === 'invalid' && (
            <div className="text-center">
              <AlertCircle className="text-red-500 mx-auto mb-3" size={40} />
              <p className="text-legal-900 dark:text-white font-semibold mb-2">Enlace inválido o expirado</p>
              <p className="text-sm text-legal-600 dark:text-legal-400 mb-4">
                Volve a solicitar el reset desde el formulario de login.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="text-legal-600 dark:text-legal-400 underline text-sm"
              >
                Ir a login
              </button>
            </div>
          )}

          {recoverySession === 'ready' && success && (
            <div className="text-center">
              <CheckCircle className="text-green-500 mx-auto mb-3" size={40} />
              <p className="text-legal-900 dark:text-white font-semibold mb-1">Contraseña actualizada</p>
              <p className="text-sm text-legal-600 dark:text-legal-400">Redirigiendo...</p>
            </div>
          )}

          {recoverySession === 'ready' && !success && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
                Nueva contraseña
              </h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 pr-12 py-3 rounded-lg border border-gray-300 dark:border-legal-600 bg-white dark:bg-legal-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-legal-500 outline-none"
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    required
                    autoFocus
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Ocultar' : 'Mostrar'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirmar contraseña
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-legal-600 bg-white dark:bg-legal-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-legal-500 outline-none"
                  placeholder="Repetí la contraseña"
                  minLength={6}
                  required
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300 text-sm">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary py-3 font-semibold disabled:opacity-50"
              >
                {isLoading ? 'Actualizando...' : 'Actualizar contraseña'}
              </button>
            </form>
          )}
        </div>
      </div>
      </div>
      <StatusBar />
    </div>
  )
}
